import React, { createContext, useContext, useState, useEffect, ReactNode, useRef, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { User, Session } from '@supabase/supabase-js';

const MFA_VERIFIED_KEY = 'admin_mfa_verified';
const BREAKGLASS_EMAIL = 'saleemrustom13@gmail.com';

interface AdminContextType {
  user: User | null;
  session: Session | null;
  isAdmin: boolean;
  isStoreManager: boolean;
  mfaVerified: boolean;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ data: any; error: any }>;
  signOut: () => Promise<void>;
  setMfaVerified: (verified: boolean) => void;
}

const AdminContext = createContext<AdminContextType | undefined>(undefined);

const LAST_ACTIVITY_KEY = 'admin_last_activity_ts';
const DEFAULT_TIMEOUT_MS = 15 * 60 * 1000;

// Cache timeout to avoid DB calls during token refresh
let cachedTimeoutMs: number | null = null;

const readLastActivityTs = (): number | null => {
  try {
    const raw = localStorage.getItem(LAST_ACTIVITY_KEY);
    if (!raw) return null;
    const parsed = Number(raw);
    if (!Number.isFinite(parsed) || parsed <= 0) return null;
    return parsed;
  } catch {
    return null;
  }
};

const clearLastActivityTs = () => {
  try {
    localStorage.removeItem(LAST_ACTIVITY_KEY);
  } catch {
    // ignore
  }
};

const fetchInactivityTimeoutMs = async (): Promise<number> => {
  // Return cached value if available (avoids DB call during token refresh)
  if (cachedTimeoutMs !== null) return cachedTimeoutMs;

  try {
    const { data, error } = await supabase
      .from('security_settings')
      .select('inactivity_timeout_minutes')
      .eq('id', 'default')
      .single();

    if (error || !data?.inactivity_timeout_minutes) {
      cachedTimeoutMs = DEFAULT_TIMEOUT_MS;
      return DEFAULT_TIMEOUT_MS;
    }

    const minutes = Number(data.inactivity_timeout_minutes);
    if (!Number.isFinite(minutes) || minutes <= 0) {
      cachedTimeoutMs = DEFAULT_TIMEOUT_MS;
      return DEFAULT_TIMEOUT_MS;
    }

    cachedTimeoutMs = minutes * 60 * 1000;
    return cachedTimeoutMs;
  } catch {
    cachedTimeoutMs = DEFAULT_TIMEOUT_MS;
    return DEFAULT_TIMEOUT_MS;
  }
};

// Synchronous inactivity check for use during token refresh/visibility events.
// Uses cached timeout when available; falls back to DEFAULT_TIMEOUT_MS.
const isInactivityExpiredSync = (): boolean => {
  const last = readLastActivityTs();
  if (!last) return false; // No activity recorded, don't block

  const timeoutMs = cachedTimeoutMs ?? DEFAULT_TIMEOUT_MS;
  const elapsed = Date.now() - last;
  return elapsed >= timeoutMs;
};

export const AdminProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isStoreManager, setIsStoreManager] = useState(false);
  const [mfaVerified, setMfaVerifiedState] = useState(() => {
    try {
      return sessionStorage.getItem(MFA_VERIFIED_KEY) === 'true';
    } catch { return false; }
  });
  const [loading, setLoading] = useState(true);

  const setMfaVerified = useCallback((verified: boolean) => {
    setMfaVerifiedState(verified);
    try {
      if (verified) {
        sessionStorage.setItem(MFA_VERIFIED_KEY, 'true');
      } else {
        sessionStorage.removeItem(MFA_VERIFIED_KEY);
      }
    } catch { /* ignore */ }
  }, []);

  // Keep privileged status in a ref so we can enforce inactivity synchronously on TOKEN_REFRESHED.
  const rolesRef = useRef<string[]>([]);
  const isPrivilegedRef = useRef(false);

  // Cross-tab immediate logout sync (storage events can be delayed/unreliable across browsers).
  const logoutChannelRef = useRef<BroadcastChannel | null>(null);

  const applyRolesToState = useCallback((roles: string[]) => {
    rolesRef.current = roles;
    const privileged = roles.includes('admin') || roles.includes('store_manager');
    isPrivilegedRef.current = privileged;

    setIsAdmin(roles.includes('admin'));
    setIsStoreManager(roles.includes('store_manager'));
  }, []);

  const forceLogout = useCallback(
    async (reason: 'inactivity' | 'cross_tab') => {
      try {
        await supabase.auth.signOut();
      } catch {
        // ignore
      }

      clearLastActivityTs();
      cachedTimeoutMs = null;
      rolesRef.current = [];
      isPrivilegedRef.current = false;

      setUser(null);
      setSession(null);
      setIsAdmin(false);
      setIsStoreManager(false);
      setMfaVerified(false);
      setLoading(false);

      // Notify other tabs to logout immediately.
      try {
        logoutChannelRef.current?.postMessage({ type: 'FORCE_LOGOUT', reason });
      } catch {
        // ignore
      }
    },
    [setMfaVerified]
  );

  useEffect(() => {
    // Setup BroadcastChannel for cross-tab logout.
    try {
      logoutChannelRef.current = new BroadcastChannel('admin-auth');
      logoutChannelRef.current.onmessage = (e) => {
        if (e?.data?.type === 'FORCE_LOGOUT') {
          // Avoid loops: if already logged out, do nothing.
          forceLogout('cross_tab');
        }
      };
    } catch {
      logoutChannelRef.current = null;
    }

    const enforceInactivityIfPrivileged = async (activeSession: Session, roles: string[]) => {
      const isPrivileged = roles.includes('admin') || roles.includes('store_manager');
      if (!isPrivileged) return false;

      const last = readLastActivityTs();
      if (!last) return false;

      const timeoutMs = await fetchInactivityTimeoutMs();
      const elapsed = Date.now() - last;

      if (elapsed >= timeoutMs) {
        await forceLogout('inactivity');
        return true;
      }

      return false;
    };

    const fetchRolesForUser = async (userId: string) => {
      const { data, error } = await supabase.from('user_roles').select('role').eq('user_id', userId);
      if (error) throw error;
      return (data?.map((r) => r.role) || []) as string[];
    };

    // Set up auth state listener
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, nextSession) => {
      // Handle sign out or token refresh failure
      if (event === 'SIGNED_OUT' || (event === 'TOKEN_REFRESHED' && !nextSession)) {
        cachedTimeoutMs = null;
        rolesRef.current = [];
        isPrivilegedRef.current = false;

        setSession(null);
        setUser(null);
        setIsAdmin(false);
        setIsStoreManager(false);
        setLoading(false);
        return;
      }

      // CRITICAL: If a privileged user is already expired, do not accept refreshed tokens.
      // (We rely on isPrivilegedRef, populated when roles were fetched.)
      if (event === 'TOKEN_REFRESHED' && nextSession?.user && isPrivilegedRef.current && isInactivityExpiredSync()) {
        // Defer side effects to avoid doing async work directly in auth callback.
        setTimeout(() => {
          void forceLogout('inactivity');
        }, 0);
        return;
      }

      // Normal state updates
      setSession(nextSession);
      setUser(nextSession?.user ?? null);

      if (nextSession?.user) {
        // Defer Supabase calls to avoid auth callback deadlocks
        setTimeout(() => {
          (async () => {
            try {
              const roles = await fetchRolesForUser(nextSession.user.id);

              // Maintenance-style enforcement: if timeout already elapsed, sign out immediately
              const didLogout = await enforceInactivityIfPrivileged(nextSession, roles);
              if (didLogout) return;

              applyRolesToState(roles);
              setLoading(false);
            } catch (error) {
              console.error('Failed to fetch user roles:', error);
              applyRolesToState([]);
              setLoading(false);
            }
          })();
        }, 0);
      } else {
        applyRolesToState([]);
        setLoading(false);
      }
    });

    // Check for existing session
    supabase.auth.getSession().then(({ data: { session: initialSession } }) => {
      setSession(initialSession);
      setUser(initialSession?.user ?? null);

      if (initialSession?.user) {
        supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', initialSession.user.id)
          .then(async ({ data, error }) => {
            if (error) {
              console.error('Failed to fetch user roles:', error);
              applyRolesToState([]);
              setLoading(false);
              return;
            }

            const roles = (data?.map((r) => r.role) || []) as string[];

            // Enforce timeout even when the user visits the storefront (outside /admin)
            const didLogout = await enforceInactivityIfPrivileged(initialSession, roles);
            if (didLogout) return;

            applyRolesToState(roles);
            setLoading(false);
          });
      } else {
        applyRolesToState([]);
        setLoading(false);
      }
    });

    // Global enforcement when tab becomes visible again (storefront or admin)
    const onVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      if (!isPrivilegedRef.current) return;
      if (!isInactivityExpiredSync()) return;

      // Defer async side-effects.
      setTimeout(() => {
        void forceLogout('inactivity');
      }, 0);
    };

    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      subscription.unsubscribe();
      try {
        logoutChannelRef.current?.close();
      } catch {
        // ignore
      }
      logoutChannelRef.current = null;
    };
  }, [applyRolesToState, forceLogout]);

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    // Seed inactivity tracking timestamp on successful sign-in
    if (!error) {
      try {
        localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()));
      } catch {
        // ignore
      }
    }

    return { data, error };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    clearLastActivityTs();
    cachedTimeoutMs = null;
    setMfaVerified(false);

    setUser(null);
    setSession(null);
    setIsAdmin(false);
    setIsStoreManager(false);
  };

  return (
    <AdminContext.Provider
      value={{
        user,
        session,
        isAdmin,
        isStoreManager,
        mfaVerified,
        loading,
        signIn,
        signOut,
        setMfaVerified,
      }}
    >
      {children}
    </AdminContext.Provider>
  );
};

export const useAdmin = () => {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error('useAdmin must be used within AdminProvider');
  }
  return context;
};

